import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FaceDetectionService from '../services/FaceDetectionService';
import AttendanceService from '../services/AttendanceService';
import { geofenceService } from '../services/GeofenceService';
import { locationService } from '../services/LocationService';
import './AttendanceScreen.css';

const AttendanceScreen: React.FC = () => {
  const navigate = useNavigate();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [location, setLocation] = useState<GeolocationPosition | null>(null);
  const [isInArea, setIsInArea] = useState(false);
  const [attendanceStatus, setAttendanceStatus] = useState<string>('outside');
  const [isScanning, setIsScanning] = useState(false);
  const [punchCode, setPunchCode] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Duty Pass states
  const [dutyPassMode, setDutyPassMode] = useState(false);
  const [dutyPassPurpose, setDutyPassPurpose] = useState("");
  const [showPurposeModal, setShowPurposeModal] = useState(false);
  const [confirmedLocation, setConfirmedLocation] = useState<{
    place: string;
    latitude: number;
    longitude: number;
  } | null>(null);

  useEffect(() => {
    const initialize = async () => {
      await initializeApp();
    };
    initialize();

    return () => {
      locationService.stopLocationTracking();
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play().catch(console.error);
      };
    }
  }, [cameraStream]);

  const initializeApp = async () => {
    try {
      // Request location permission
      const locationPermission = await locationService.requestPermission();

      if (locationPermission) {
        setHasPermission(true);
        startLocationTracking();
      } else {
        setHasPermission(false);
        alert('Location permission is required for attendance tracking.');
      }
    } catch (error) {
      console.error('Error initializing app:', error);
      setHasPermission(false);
    }
  };

  const startLocationTracking = async () => {
    try {
      const initialLocation = await locationService.getCurrentLocation();
      setLocation(initialLocation);
      checkGeofence(initialLocation);

      locationService.startLocationTracking((newLocation) => {
        console.log('Location updated:', newLocation.coords);
        setLocation(newLocation);
        checkGeofence(newLocation);
      });
    } catch (error) {
      console.error('Error starting location tracking:', error);
    }
  };

  const checkGeofence = async (currentLocation: GeolocationPosition) => {
    try {
      const inArea = await geofenceService.isInDesignatedArea(currentLocation);
      console.log(`Geofence check: ${inArea ? 'INSIDE' : 'OUTSIDE'} area`);

      if (inArea && !isInArea) {
        console.log('ENTERING designated area');
        setIsInArea(true);
        setAttendanceStatus('entering');
      } else if (!inArea && isInArea) {
        console.log('EXITING designated area');
        setIsInArea(false);
        setAttendanceStatus('exiting');
        await handleAreaExit(currentLocation);
      }
    } catch (error) {
      console.error('Error in geofence check:', error);
    }
  };

  const handleAreaExit = async (currentLocation: GeolocationPosition) => {
    if (!punchCode || punchCode.trim() === '') {
      alert('Please enter a punch code before leaving the area.');
      return;
    }

    console.log('Area exit with location:', currentLocation.coords);
    alert('Leaving Designated Area - Marking exit attendance with face verification...');

    try {
      const exitResult = await FaceDetectionService.markExit(
        punchCode,
        currentLocation.coords.latitude,
        currentLocation.coords.longitude
      );

      if (exitResult.status === 'success') {
        alert(`✅ Exit marked successfully! ${exitResult.message}`);
      } else {
        alert(`❌ Exit marking failed: ${exitResult.message}`);
      }
    } catch (error: any) {
      console.error('Error marking exit:', error);
      const errorMessage = error.message || 'Error marking exit. Please try again.';
      alert(`❌ Exit marking failed: ${errorMessage}`);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const captureImageFromVideo = (): string | null => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas ref not available');
      return null;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      console.error('Canvas context not available');
      return null;
    }

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    // Draw the current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to base64 image
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);

    // Extract base64 data (remove data:image/jpeg;base64, prefix)
    const base64Data = imageDataUrl.split(',')[1];

    console.log('Image captured, base64 length:', base64Data.length);
    return base64Data;
  };

  const scanFaces = async () => {
    if (!punchCode || punchCode.trim() === '') {
      alert('Please enter a punch code before scanning.');
      return;
    }

    if (!location) {
      alert('Location not available. Please wait for location to be determined.');
      return;
    }

    if (!showCamera || !cameraStream) {
      alert('Please start the camera first.');
      return;
    }

    setIsScanning(true);

    try {
      console.log('=== STARTING FACE DETECTION AND ATTENDANCE MARKING ===');
      console.log('SCAN: Using punch code:', punchCode);
      console.log('SCAN: Location:', location.coords);

      // Capture image from video
      const imageBase64 = captureImageFromVideo();

      if (!imageBase64) {
        throw new Error('Failed to capture image from camera');
      }

      console.log('SCAN: Image captured, sending to FaceDetectionService...');

      // Use FaceDetectionService to detect faces and mark attendance
      const result = await FaceDetectionService.detectFaces(
        imageBase64,
        punchCode,
        location.coords.latitude,
        location.coords.longitude
      );

      console.log('SCAN: Face detection result:', result);

      if (result.success && result.backendResponse) {
        // Success - show success message
        let successMessage = `✅ ${result.backendResponse.message || 'Attendance marked successfully!'}`;

        if (result.faces && result.faces.length > 0) {
          successMessage += `\n\nFaces detected: ${result.faces.length}`;

          // Check if any face was verified
          const verifiedFaces = result.faces.filter((face: any) => face.verified);
          if (verifiedFaces.length > 0) {
            successMessage += `\nVerified faces: ${verifiedFaces.length}`;
          }
        }

        alert(successMessage);

        // Store attendance record locally for history
        const attendanceRecord = {
          id: Date.now().toString(),
          type: "entry" as const,
          timestamp: new Date().toISOString(),
          location: {
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
          },
          punchCode: punchCode,
          backendResponse: result.backendResponse.data || result.backendResponse,
          backendSuccess: true,
          faceDetected: result.faces.length > 0,
          facesCount: result.faces.length,
        };

        // Save to local storage for history
        const existingRecords = JSON.parse(localStorage.getItem("attendance_records") || "[]");
        const updatedRecords = [attendanceRecord, ...existingRecords];
        localStorage.setItem("attendance_records", JSON.stringify(updatedRecords));

      } else {
        // Failure - show error message
        let errorMessage = `❌ ${result.backendResponse?.message || 'Face detection failed'}`;

        if (result.faces.length === 0) {
          errorMessage += `\n\nNo faces detected in the image. Please ensure your face is clearly visible.`;
        }

        alert(errorMessage);

        // Store failed attempt for history
        const attendanceRecord = {
          id: Date.now().toString(),
          type: "entry" as const,
          timestamp: new Date().toISOString(),
          location: {
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
          },
          punchCode: punchCode,
          backendResponse: result.backendResponse,
          backendSuccess: false,
          faceDetected: false,
          facesCount: 0,
        };

        const existingRecords = JSON.parse(localStorage.getItem("attendance_records") || "[]");
        const updatedRecords = [attendanceRecord, ...existingRecords];
        localStorage.setItem("attendance_records", JSON.stringify(updatedRecords));
      }

    } catch (error) {
      console.error('Error in face detection and attendance marking:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Error during face detection: ${errorMessage}`);
    } finally {
      setIsScanning(false);
    }
    stopCamera();
  };

  const startCamera = async () => {
    try {
      console.log('Starting camera...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });
      console.log('Camera stream obtained:', stream);
      setCameraStream(stream);
      setShowCamera(true);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          console.log('Video metadata loaded, playing...');
          videoRef.current?.play().catch(e => console.error('Error playing video:', e));
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please check permissions.');
    }
  };

  const getStatusColor = () => {
    switch (attendanceStatus) {
      case 'entering':
        return '#4CAF50';
      case 'exiting':
        return '#FF9800';
      case 'inside':
        return '#2196F3';
      default:
        return '#2f97bd';
    }
  };

  const getStatusText = () => {
    switch (attendanceStatus) {
      case 'entering':
        return 'ENTERING DESIGNATED AREA';
      case 'exiting':
        return 'EXITING DESIGNATED AREA';
      case 'inside':
        return 'INSIDE DESIGNATED AREA';
      default:
        return 'DESIGNATED AREA';
    }
  };

  const getCurrentLocationInfo = () => {
    if (location) {
      const info = `Punch Code: ${punchCode || 'Not Set'}
Latitude: ${location.coords.latitude.toFixed(6)}
Longitude: ${location.coords.longitude.toFixed(6)}
Accuracy: ${location.coords.accuracy?.toFixed(2)}m
Timestamp: ${new Date(location.timestamp).toLocaleTimeString()}`;
      alert(info);
    } else {
      alert('Location Info: No location data available');
    }
  };

  const getPlaceNameFromCoordinates = async (latitude: number, longitude: number): Promise<string> => {
    try {
      // Reverse geocoding using Nominatim (open-source)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
      );
      const data = await response.json();

      // Extract place name from the response
      const placeName = data?.display_name || data.address?.city || data.address?.town || data.address?.village || data.name || "Unknown Location";
      return placeName;
    } catch (error) {
      console.error("Error getting place name:", error);
      return "Unknown Location";
    }
  };

  const handleDutyPass = async () => {
    try {
      if (!punchCode || punchCode.trim() === "") {
        alert("Please enter a punch code before applying for duty pass.");
        return;
      }

      setDutyPassMode(true);

      // Get current location
      const currentLoc = await locationService.getCurrentLocation();
      if (!currentLoc) {
        alert("Location Error: Unable to get current location. Please ensure location services are enabled.");
        setDutyPassMode(false);
        return;
      }

      const { latitude, longitude } = currentLoc.coords;

      // Get place name from coordinates
      const place = await getPlaceNameFromCoordinates(latitude, longitude);

      // Confirm location
      if (window.confirm(`📍 Duty Pass - Confirm Location\n\nPlace: ${place}\nLatitude: ${latitude.toFixed(6)}\nLongitude: ${longitude.toFixed(6)}\n\nDo you want to proceed?`)) {
        setConfirmedLocation({ place, latitude, longitude });
        setShowPurposeModal(true);
      } else {
        setDutyPassMode(false);
      }
    } catch (error) {
      console.error("Error in duty pass:", error);
      alert("Error: An error occurred while processing duty pass. Please try again.");
      setDutyPassMode(false);
    }
  };

  const handlePurposeSubmit = async () => {
    if (!dutyPassPurpose || dutyPassPurpose.trim() === "") {
      alert("Purpose Required: Please enter the purpose for duty pass.");
      return;
    }

    if (!confirmedLocation) {
      alert("Error: Location information is missing. Please try again.");
      setShowPurposeModal(false);
      setDutyPassMode(false);
      return;
    }

    setShowPurposeModal(false);
    setIsScanning(true); // Re-use scanning state for loading UI

    try {
      // Ensure camera is active and capture image
      let imageBase64 = captureImageFromVideo();

      if (!imageBase64) {
        // If camera wasn't active, try starting it briefly? 
        // Better to require camera to be started first as per current UI flow
        alert("Please start the camera first to capture your photo for the duty pass.");
        setIsScanning(false);
        setDutyPassMode(false);
        return;
      }

      console.log("DUTY PASS: Sending to API...");

      // Convert base64 to Blob
      const base64Response = await fetch(`data:image/jpeg;base64,${imageBase64}`);
      const blob = await base64Response.blob();

      // Call the duty pass API
      const apiResult = await FaceDetectionService.applyDutyPass(
        punchCode,
        blob,
        dutyPassPurpose,
        {
          latitude: confirmedLocation.latitude,
          longitude: confirmedLocation.longitude,
          address: confirmedLocation.place
        }
      );

      console.log("DUTY PASS: API response:", apiResult);

      // Save duty pass attendance to local storage
      const currentLoc = await locationService.getCurrentLocation();
      if (currentLoc) {
        await AttendanceService.markDutyPass(
          currentLoc,
          punchCode,
          dutyPassPurpose,
          confirmedLocation.place,
          apiResult
        );
      }

      // Show success message
      alert(`✅ Duty Pass Attendance Marked!\n\nPlace: ${confirmedLocation.place}\nPurpose: ${dutyPassPurpose}\n\nBackend: ${apiResult.message || "Success"}`);

      setDutyPassPurpose("");
      setConfirmedLocation(null);
      setDutyPassMode(false);
      stopCamera();
    } catch (error: any) {
      console.error("Error marking duty pass attendance:", error);
      alert(`❌ Duty Pass Failed: ${error.message || "An error occurred. Please try again."}`);
      setDutyPassMode(false);
    } finally {
      setIsScanning(false);
    }
  };

  if (hasPermission === null) {
    return (
      <div className="attendance-screen">
        <div className="loading-container">
          <h2>Requesting permissions...</h2>
        </div>
      </div>
    );
  }

  if (hasPermission === false) {
    return (
      <div className="attendance-screen">
        <div className="error-container">
          <h2>No access to location</h2>
          <p>Location permission is required for this app to work.</p>
          <button className="btn btn-primary" onClick={initializeApp}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="attendance-screen">
      <div className="header">
        <div className="header-title">
          {/* <div className="logo">
            <img src="/images/Frame 3.png" alt="CDIT Logo" />
          </div> */}
          <h1>CDIT Smart - Attendance</h1>
        </div>
        <div className="nav-buttons">
          <button className="nav-button" onClick={() => navigate('/history')}>
            {/* <img src="/images/history.png" alt="history" style={{width: '20px', height: '20px'}} /> */}
            History
          </button>
          <button className="nav-button" onClick={getCurrentLocationInfo}>
            {/* <img src="/images/info.png" alt="info" style={{width: '25px', height: '25px'}} /> */}
            Info
          </button>
        </div>
      </div>

      <div className="main-content">
        <div className="left-panel">
          <div className="punch-code-input">
            {/* <label className="form-label">Punch Code:</label> */}
            <input
              type="text"
              value={punchCode}
              onChange={(e) => setPunchCode(e.target.value)}
              placeholder="Enter punch code"
              className="form-input"
              autoCapitalize="none"
              autoCorrect="off"
            />
            {location && (
              <div className="location-info" style={{ marginTop: '15px' }}>
                <img src="/images/pin.gif" alt="location pin" style={{ width: '30px', height: '30px', marginRight: '8px', verticalAlign: 'middle' }} />
                Latitude: {location.coords.latitude.toFixed(6)},
                Longitude: {location.coords.longitude.toFixed(6)}
              </div>
            )}
          </div>
        </div>

        <div className="right-panel">
          <div className="camera-container">
            {showCamera ? (
              <div className="camera-placeholder">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <div className="camera-overlay">
                  {isScanning ? 'Detecting Face & Verifying Employee...' : 'Camera Active - Position your face in frame'}
                </div>
              </div>
            ) : (
              <div className="camera-placeholder" style={{
                backgroundImage: 'url(/images/bg_4.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}>
                <div style={{
                  backgroundColor: '#c7c3c3b3',
                  // padding: '1px',
                  borderRadius: '15px',
                  color: 'black',
                  textAlign: 'center',
                  marginTop: '250px'
                }}>
                  {/* <img src="/images/camera.gif" alt="camera ready" style={{width: '80px', height: '80px',marginBottom: '-18px',padding:'3px'}} /> */}

                  <div>
                    {/* <img src="/images/camera.gif" alt="camera ready" style={{width: '60px', height: '60px',marginBottom: '-18px',padding:'3px'}} /> */}
                    {/* Camera Ready */}
                  </div>
                  {/* <p>Click "Start Camera" to begin face detection</p> */}
                </div>
              </div>
            )}
          </div>

          <div className="controls">
            {!showCamera ? (
              <button className="control-button verify" onClick={startCamera}>
                <img src="/images/camera.gif" alt="scanning" style={{ width: '45px', height: '45px' }} />
                Start Camera
              </button>
            ) : (
              <div className="button-group">
                <button
                  className={`control-button verify ${isScanning ? 'disabled' : ''}`}
                  onClick={scanFaces}
                  disabled={isScanning}
                >
                  {isScanning ? (
                    <img src="/images/security.png" alt="scanning" style={{ width: '45px', height: '45px' }} />
                  ) : (
                    <img src="/images/security.png" alt="scanning" style={{ width: '45px', height: '45px' }} />
                  )}
                  {isScanning ? 'Detecting Face...' : 'Mark Attendance'}
                </button>

                <button
                  className={`control-button duty-pass ${isScanning || dutyPassMode ? 'disabled' : ''}`}
                  onClick={handleDutyPass}
                  disabled={isScanning || dutyPassMode}
                >
                  <img src="/images/pin.gif" alt="duty pass" style={{ width: '45px', height: '45px' }} />
                  {dutyPassMode ? 'In Progress...' : 'Duty Pass'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Duty Pass Purpose Modal */}
      {showPurposeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Enter Purpose for Duty Pass</h3>
            {confirmedLocation && (
              <p className="modal-subtitle">Location: {confirmedLocation.place}</p>
            )}
            <textarea
              className="modal-textarea"
              value={dutyPassPurpose}
              onChange={(e) => setDutyPassPurpose(e.target.value)}
              placeholder="Enter purpose for duty pass..."
              rows={4}
            />
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowPurposeModal(false);
                  setDutyPassMode(false);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handlePurposeSubmit}
              >
                Mark Attendance
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceScreen; 